import { Link } from 'react-router-dom'
import EmptyState from '@/components/shared/EmptyState'
import StatCard from '@/components/shared/StatCard'
import { formatCurrency, formatDate, r2 } from '@/lib/utils'
import { IconReceipt, IconClock, IconCheckCircle, IconAlertTriangle, IconEye, IconEdit } from '@/components/icons/Icons'
import type { Factura } from '@/lib/types'

interface TabFacturacionProps {
  facturas: Factura[]
  empresaId: number
}

const estadoBadge: Record<string, string> = {
  pre_factura: 'badge-borrador',
  borrador: 'badge-borrador',
  emitida: 'badge-enviada',
  pagada: 'badge-aceptada',
  anulada: 'badge-rechazada',
}

const estadoLabel: Record<string, string> = {
  pre_factura: 'Borrador',
  borrador: 'Borrador',
  emitida: 'Emitida',
  pagada: 'Pagada',
  anulada: 'Anulada',
}

export default function TabFacturacion({ facturas, empresaId }: TabFacturacionProps) {
  const totalFacturado = r2(facturas.filter(f => f.estado !== 'anulada').reduce((s, f) => s + f.total, 0))
  const totalPagado = r2(facturas.filter(f => f.estado === 'pagada').reduce((s, f) => s + f.total, 0))
  const totalPendiente = r2(facturas.filter(f => f.estado === 'emitida' || f.estado === 'pre_factura').reduce((s, f) => s + f.total, 0))

  const hoy = new Date().toISOString().split('T')[0]
  const totalVencido = r2(
    facturas
      .filter(f => (f.estado === 'emitida' || f.estado === 'pre_factura') && f.fecha_vencimiento && f.fecha_vencimiento < hoy)
      .reduce((s, f) => s + f.total, 0)
  )

  if (facturas.length === 0) {
    return (
      <EmptyState
        icon={<IconReceipt size={40} />}
        title="Sin cobros"
        description="Esta empresa aun no tiene cobros registrados."
        actionLabel="Nuevo Cobro"
        onAction={() => window.location.href = `/facturas/nueva?cliente_id=${empresaId}`}
      />
    )
  }

  return (
    <div>
      <div className="empresa-360-stats">
        <StatCard
          icon={<IconReceipt size={20} />}
          label="Total Facturado"
          value={formatCurrency(totalFacturado, 'USD')}
          color="primary"
        />
        <StatCard
          icon={<IconCheckCircle size={20} />}
          label="Pagado"
          value={formatCurrency(totalPagado, 'USD')}
          color="success"
        />
        <StatCard
          icon={<IconClock size={20} />}
          label="Pendiente"
          value={formatCurrency(totalPendiente, 'USD')}
          color="warning"
        />
        <StatCard
          icon={<IconAlertTriangle size={20} />}
          label="Vencido"
          value={formatCurrency(totalVencido, 'USD')}
          color="danger"
        />
      </div>

      <div className="table-container" style={{ marginTop: 20 }}>
        <table>
          <thead>
            <tr>
              <th>Numero</th>
              <th>Fecha</th>
              <th>Vencimiento</th>
              <th>Monto</th>
              <th>Estado</th>
              <th style={{ width: 100 }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {facturas.map(f => (
              <tr key={f.id}>
                <td style={{ fontWeight: 700, fontFamily: 'monospace' }}>
                  {f.numero}
                  {f.nombre_interno && (
                    <div style={{ fontWeight: 400, fontFamily: 'inherit', fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: 2 }}>
                      {f.nombre_interno}
                    </div>
                  )}
                </td>
                <td>{formatDate(f.fecha_emision)}</td>
                <td>{formatDate(f.fecha_vencimiento)}</td>
                <td style={{ fontWeight: 700 }}>{formatCurrency(f.total, f.moneda)}</td>
                <td>
                  <span className={`badge ${estadoBadge[f.estado] || ''}`}>
                    {estadoLabel[f.estado] || f.estado}
                  </span>
                </td>
                <td>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <Link to={`/facturas/${f.id}`} className="btn btn-sm btn-secondary" title="Ver">
                      <IconEye size={14} />
                    </Link>
                    <Link to={`/facturas/${f.id}/editar`} className="btn btn-sm btn-secondary" title="Editar">
                      <IconEdit size={14} />
                    </Link>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
